<template>
  <template v-if="$route.name === 'canteen.products'">
    <div class="col-12 col-xl-12">
      <div class="d-flex justify-content-end mb-3">
        <router-link :to="{ name: 'canteen.products.create' }" class="btn btn-secondary btn-sm m-4" v-can="'canteen_products.create'">
          <i class="fas fa-plus me-2"></i>
          {{ t('canteen.add_product') }}
        </router-link>
      </div>

      <div class="card mb-5" style="margin-top: 1rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3">{{ t('canteen.manage_products') }}</h6>
            </div>
          </div>
        </div>

        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-2 p-md-4">
            <LoadingSpinner v-if="loading" :message="t('canteen.loading_products')" />
            
            <template v-else>
              <div class="row">
                <div v-for="product in products" :key="product.id" class="col-md-4 mb-4">
                  <div class="card product-card">
                    <img 
                      :src="getProductImage(product)" 
                      class="card-img-top product-image" 
                      :alt="product.name"
                      @error="handleImageError"
                    >
                    <div class="card-body">
                      <h5 class="card-title">{{ product.name }}</h5>
                      <p class="card-text text-muted">{{ product.description }}</p>
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="h5 text-primary">{{ product.price }} {{ t('canteen.lyd_abbr') }}</span>
                        <span class="badge" :class="product.is_available ? 'bg-success' : 'bg-secondary'">
                          {{ product.is_available ? t('canteen.available') : t('canteen.not_available') }}
                        </span>
                      </div>
                      <div v-if="product.allergy_warnings" class="mt-2">
                        <small class="text-danger">
                          <i class="fas fa-exclamation-triangle"></i>
                          {{ t('canteen.allergy_warnings_label') }}: {{ product.allergy_warnings }}
                        </small>
                      </div>
                      <div class="mt-3 d-flex gap-2">
                        <router-link 
                          :to="{ name: 'canteen.products.edit', params: { id: product.id } }" 
                          class="btn btn-sm btn-outline-primary"
                          v-can="'canteen_products.update'"
                        >
                          <i class="fas fa-edit"></i>
                        </router-link>
                        <button 
                          @click="toggleAvailability(product)" 
                          class="btn btn-sm"
                          :class="product.is_available ? 'btn-outline-warning' : 'btn-outline-success'"
                          v-can="'canteen_products.update'"
                        >
                          <i class="fas" :class="product.is_available ? 'fa-eye-slash' : 'fa-eye'"></i>
                        </button>
                        <button 
                          @click="confirmDelete(product)" 
                          class="btn btn-sm btn-outline-danger"
                          v-can="'canteen_products.delete'"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="products.length === 0" class="text-center py-5">
                <i class="fas fa-utensils fa-3x text-muted mb-3"></i>
                <p class="text-muted">{{ t('canteen.no_products') }}</p>
                <router-link :to="{ name: 'canteen.products.create' }" class="btn btn-primary"
                v-can="'canteen_products.create'">
                  {{ t('canteen.add_first_product') }}
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
      
      <DeleteDialog
        ref="deleteDialog"
        entity-name="CanteenProduct"
        :entity-label="t('canteen.product')"
        @delete="handleDelete"
      />
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import DeleteDialog from '@/components/common/DeleteDialog.vue'

// ✅ الصورة الافتراضية
import defaultProductImage from '@/assets/img/default-product.png'

const { t, locale } = useI18n()
const toast = useToast()
const products = ref([])
const loading = ref(false)
const deleteDialog = ref(null)
const selectedProduct = ref(null)

const DEFAULT_PRODUCT_IMAGE = defaultProductImage

function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden'));
  return toast.error(extractApiMessage(error, fallbackKey));
}

const fetchProducts = async () => {
  loading.value = true;
  try {
    const resp = await api.get('/canteen/products');
    const status = resp?.status ?? resp?.data?.status ?? null;

    if (!status || status < 200 || status >= 300) {
      if (status === 403) return toast.error(t('auth.forbidden'));
      return toast.error(resp?.data?.message || t('canteen.load_failed'));
    }

    products.value = Array.isArray(resp.data) ? resp.data : (resp.data?.data ?? []);
  } catch (error) {
    notifyError(error, 'canteen.load_failed');
  } finally {
    loading.value = false;
  }
};

// ✅ أهم تعديل هنا
const getProductImage = (product) => {
  if (product.image_url) return product.image_url

  if (product.image && product.image.startsWith('announcements/')) {
    return `/storage/${product.image}`
  }

  if (product.image) return product.image

  // 👇 fallback
  return DEFAULT_PRODUCT_IMAGE
}

// ✅ fallback لو الصورة ضربت
const handleImageError = (event) => {
  event.target.src = DEFAULT_PRODUCT_IMAGE
}

const toggleAvailability = async (product) => {
  try {
    const resp = await api.put(`/canteen/products/${product.id}`, {
      is_available: !product.is_available
    });

    const status = resp?.status ?? resp?.data?.status ?? null;
    if (!status || status < 200 || status >= 300) {
      if (status === 403) return toast.error(t('auth.forbidden'));
      return toast.error(resp?.data?.message || t('canteen.product_toggle_failed'));
    }

    product.is_available = resp?.data?.is_available ?? !product.is_available;
    toast.success(
      product.is_available ? t('canteen.product_enabled') : t('canteen.product_disabled')
    );
  } catch (error) {
    notifyError(error, 'canteen.product_toggle_failed');
  }
};

const confirmDelete = (product) => {
  selectedProduct.value = product
  deleteDialog.value.openDialog(product.id, product.name)
}

const handleDelete = async (productId) => {
  try {
    const resp = await api.delete(`/canteen/products/${productId}`);
    const status = resp?.status ?? resp?.data?.status ?? null;

    if (!status || status < 200 || status >= 300) {
      if (status === 403) return toast.error(t('auth.forbidden'));
      return toast.error(resp?.data?.message || t('canteen.product_delete_failed'));
    }

    toast.success(resp?.data?.message || t('canteen.product_deleted'));
    fetchProducts();
  } catch (error) {
    notifyError(error, 'canteen.product_delete_failed');
  }
};

onMounted(() => {
  fetchProducts()
})
</script>
<style scoped>
.product-card {
  transition: transform 0.2s;
  height: 100%;
}

.product-card:hover {
  transform: translateY(-5px);
}

.product-image {
  height: 200px;
  object-fit: cover;
  width: 100%;
}

.d-flex.justify-content-end {
  margin-top: 1rem;
}

.btn-primary {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}
</style>